import type { Capability } from '@clawharbor/manifest';
import { html, SP } from '../../html.js';
import type { VNode } from '../../html.js';
import type { DashboardActions } from '../store.js';
import { fmtTtl } from './bits.js';

/**
 * Where a tool is served, and how long its answers may be reused.
 *
 * Each switch is labelled with the surface it governs and the URL that surface
 * lives at, because "web" and "app" mean nothing on their own and an operator
 * turning one off is deciding which clients lose the tool. The MCP Apps
 * visibility array is two independent checkboxes rather than one switch: `model`
 * and `app` are separate audiences in the extension spec — the model may call a
 * tool the app never shows, and vice versa — and collapsing them would misreport
 * what is published.
 */

/** Sensible ceilings for a read-through cache in front of someone else's site. */
const TTL_CHOICES = [60, 300, 900, 1800, 3600, 21_600, 86_400];

function Switch(props: {
  id: string;
  on: boolean;
  label: VNode | string;
  gloss: VNode | string;
  disabled?: boolean;
  onChange: (on: boolean) => void;
}): VNode {
  return html`
    <label class="optrow" for=${props.id}>
      <input
        id=${props.id}
        class="optrow__box"
        type="checkbox"
        checked=${props.on}
        disabled=${props.disabled === true}
        onChange=${(e: Event) => props.onChange((e.target as HTMLInputElement).checked)}
      />
      <span class="optrow__text">
        <span class="optrow__label">${props.label}</span>
        <span class="optrow__gloss">${props.gloss}</span>
      </span>
    </label>
  `;
}

export function Surfaces(props: {
  cap: Capability;
  tenantId: string;
  saving: boolean;
  actions: DashboardActions;
}): VNode {
  const { cap, tenantId, actions } = props;
  const web = cap.surfaces?.web?.register !== false;
  const app = cap.surfaces?.app?.register !== false;
  const visibility = cap.surfaces?.app?.visibility ?? ['model', 'app'];
  const ttl = cap.freshness?.ttlSec ?? 1800;
  const choices = TTL_CHOICES.includes(ttl) ? TTL_CHOICES : [...TTL_CHOICES, ttl].sort((a, b) => a - b);

  return html`
    <div class="surfaces">
      <div class="surfaces__col">
        <p class="cardlabel">Surfaces</p>
        ${Switch({
          id: `web-${cap.name}`,
          on: web,
          label: 'WebMCP page',
          gloss: html`Registered on <code>document.modelContext</code> at${SP}
            <code>/t/${tenantId}</code> for any browser agent that opens the page.`,
          disabled: props.saving,
          onChange: (on) => actions.setSurface(cap.name, 'web', on),
        })}
        ${Switch({
          id: `app-${cap.name}`,
          on: app,
          label: 'MCP Apps',
          gloss: html`Rendered as a <code>ui://</code> resource inside a chat client that supports
            the MCP Apps extension. The tool itself stays callable on${SP}
            <code>/t/${tenantId}/mcp</code> either way.`,
          disabled: props.saving,
          onChange: (on) => actions.setSurface(cap.name, 'app', on),
        })}
      </div>

      <div class="surfaces__col">
        <p class="cardlabel">MCP Apps visibility</p>
        <div class="vis" role="group" aria-label=${`MCP Apps visibility for ${cap.name}`}>
          ${(['model', 'app'] as const).map((target) =>
            Switch({
              id: `vis-${target}-${cap.name}`,
              on: visibility.includes(target),
              label: target,
              gloss:
                target === 'model'
                  ? 'The model may call it.'
                  : 'The app may show it in its own UI.',
              disabled: props.saving || !app,
              onChange: (on) => actions.setVisibility(cap.name, target, on),
            }),
          )}
        </div>
        ${!app
          ? html`<p class="surfaces__note">
              Visibility applies only while MCP Apps is on.
            </p>`
          : null}

        <p class="cardlabel">Freshness</p>
        <label class="ttl" for=${`ttl-${cap.name}`}>
          <span class="ttl__label">Cache answers for</span>
          <select
            id=${`ttl-${cap.name}`}
            class="ttl__select"
            disabled=${props.saving}
            onChange=${(e: Event) =>
              actions.setTtl(cap.name, Number((e.target as HTMLSelectElement).value))}
          >
            ${choices.map(
              (sec) => html`<option value=${String(sec)} selected=${sec === ttl}>
                ${fmtTtl(sec)}
              </option>`,
            )}
          </select>
        </label>
        <p class="surfaces__note">
          Every call past the TTL reaches the origin. Nothing is served from a copy older than
          ${cap.freshness?.staleOnError === false
            ? 'this'
            : html`this, except over a dead origin, where a cached answer up to${SP}
              ${fmtTtl(cap.freshness?.maxStaleSec ?? 86_400)} old is served and labelled stale`}.
        </p>
      </div>
    </div>
  `;
}
